// SystemStatus — ambient Apple-style health dot for the nav-rail foot.
//
// Replaces the user avatar position in the bottom-right of the nav rail.
// Idle = a small subtle dot with soft pulse, no labels. Hover = tooltip.
// Click = small popover (3–4 lines), no charts, no logs.
//
// Polls every 15 s:
//   - navigator.onLine                       → 'offline' (highest priority)
//   - PostgREST anon ping (cheap GET)        → 'error' on non-2xx
//   - response time vs threshold             → 'degraded' if >1500 ms
//   - else                                   → 'healthy'
//
// State priority: offline > error > degraded > healthy.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SystemStatus.css';

// Probes `staff_directory` (a safe view — anon-readable, no PINs/email/
// audit columns). Mig 139 (C1, 2026-05-10) dropped `code` from the view,
// so probe `name` instead. Hitting `access_codes` directly returns 401
// because anon no longer has SELECT on it.
const PING_URL  = import.meta.env.VITE_AWS_API_URL ? `${import.meta.env.VITE_AWS_API_URL}/staff_directory?select=name&limit=1` : null;
const API_KEY   = import.meta.env.VITE_AWS_API_KEY;
const INTERVAL  = 15_000;
const SLOW_MS   = 1500;
const TIMEOUT   = 8_000;

const STATES = {
  healthy:  { color: '#00A86B', tip: 'All systems operational',  net: 'Online',  svc: 'Operational', api: 'OK' },
  degraded: { color: '#D4A547', tip: 'Service running slow',      net: 'Online',  svc: 'Degraded',    api: 'Slow' },
  error:    { color: '#DC2626', tip: 'API unavailable',           net: 'Online',  svc: 'Down',        api: 'Error' },
  offline:  { color: '#4A5FBF', tip: 'Connection lost',           net: 'Offline', svc: '—',          api: '—' },
};

function relTime(tsMs) {
  if (!tsMs) return '—';
  const d = Math.max(1, Math.floor((Date.now() - tsMs) / 1000));
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  return `${Math.floor(d / 3600)}h ago`;
}

export default function SystemStatus() {
  const [state, setState] = useState('healthy');
  const [lastCheck, setLastCheck] = useState(0);
  const [open, setOpen] = useState(false);
  // Position computed at click time from the dot's rect — fixed coords
  // so the popover escapes any parent overflow:hidden / clipping.
  const [popPos, setPopPos] = useState(null);
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  function togglePop() {
    if (open) { setOpen(false); return; }
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    // Anchor the popover's right edge to the dot's right edge so it always
    // opens leftward (the nav rail sits flush to the screen's right edge,
    // so any leftward growth stays in frame).
    setPopPos({
      bottom: window.innerHeight - r.top + 12,
      right: Math.max(8, window.innerWidth - r.right - 2),
    });
    setOpen(true);
  }

  // Click-outside closes the popover. Both the dot wrap AND the portalled
  // popover count as "inside" — without the second check, clicking on the
  // popover itself would dismiss it.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        if (!cancelled) { setState('offline'); setLastCheck(Date.now()); }
        return;
      }
      if (!PING_URL) {
        // No API URL configured — don't pretend we can probe.
        if (!cancelled) { setState('error'); setLastCheck(Date.now()); }
        return;
      }
      const ctl = new AbortController();
      const t = setTimeout(() => ctl.abort(), TIMEOUT);
      const start = performance.now();
      try {
        const res = await fetch(PING_URL, {
          method: 'GET',
          signal: ctl.signal,
          headers: API_KEY ? { 'x-moreece-app': API_KEY } : undefined,
        });
        clearTimeout(t);
        const ms = performance.now() - start;
        if (cancelled) return;
        if (!res.ok) setState('error');
        else if (ms > SLOW_MS) setState('degraded');
        else setState('healthy');
        setLastCheck(Date.now());
      } catch {
        clearTimeout(t);
        if (!cancelled) { setState('error'); setLastCheck(Date.now()); }
      }
    }

    check();
    const id = setInterval(check, INTERVAL);
    // Also re-check on visibility change so the dot updates the moment
    // the user comes back to the tab.
    function onVis() { if (document.visibilityState === 'visible') check(); }
    document.addEventListener('visibilitychange', onVis);
    function onOnline() { check(); }
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOnline);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOnline);
    };
  }, []);

  const s = STATES[state];

  return (
    <div ref={wrapRef} className="sys-status-wrap" aria-label={s.tip}>
      <button
        type="button"
        className={`sys-status-dot is-${state}`}
        onClick={togglePop}
        title={s.tip}
        aria-label={s.tip}
        style={{ background: s.color }}
      >
        <span className="sys-status-pulse" style={{ background: s.color }} />
      </button>
      {open && popPos && createPortal(
        <div
          ref={popRef}
          className="sys-status-pop"
          role="dialog"
          style={{ position: 'fixed', bottom: popPos.bottom, right: popPos.right }}
        >
          <div className="sys-status-row"><span>Network</span><span>{s.net}</span></div>
          <div className="sys-status-row"><span>Services</span><span>{s.svc}</span></div>
          <div className="sys-status-row"><span>API</span><span>{s.api}</span></div>
          <div className="sys-status-row sys-status-foot"><span>Last check</span><span>{relTime(lastCheck)}</span></div>
        </div>,
        document.body,
      )}
    </div>
  );
}
